// usePrice — live XAU/USD price via Twelve Data free API.
// Polls every 30 seconds during London/NY sessions, every 2 min otherwise.
// Persists last known price to localStorage so JARVIS always has context.

import { useEffect, useCallback, useRef } from 'react';
import { useJarvisContext, A } from '../context/JarvisContext';
import { setCurrentPrice } from '../services/storage';
import { getCurrentSession } from '../utils/dateUtils';
import { checkPriceAlerts, checkSessionAlerts, getNotifPrefs } from '../services/notificationService';

const TWELVE_DATA_KEY = process.env.REACT_APP_TWELVE_DATA_KEY;
const SYMBOL          = 'XAU/USD';

const getPollInterval = () => {
  const session = getCurrentSession();
  if (!session) return 120_000;
  if (['London', 'New York', 'London+NY'].includes(session.name)) return 30_000;
  if (session.name === 'Pre-London') return 60_000;
  return 120_000;
};

export const usePrice = () => {
  const { state, dispatch } = useJarvisContext();
  const intervalRef = useRef(null);
  const retriesRef  = useRef(0); // ref avoids recreating fetchPrice on each retry

  const fetchPrice = useCallback(async () => {
    if (!TWELVE_DATA_KEY || TWELVE_DATA_KEY === 'your_actual_key_here') {
      dispatch({ type: A.PRICE_ERROR, payload: 'Twelve Data key not set in .env' });
      return;
    }

    dispatch({ type: A.PRICE_LOADING });

    try {
      const url  = `https://api.twelvedata.com/price?symbol=${SYMBOL}&apikey=${TWELVE_DATA_KEY}`;
      const res  = await fetch(url);
      const data = await res.json();

      if (data.status === 'error' || !data.price) {
        throw new Error(data.message || 'Invalid response from Twelve Data');
      }

      const price = parseFloat(data.price);
      dispatch({ type: A.PRICE_UPDATE, payload: { price } });
      setCurrentPrice(price);
      retriesRef.current = 0;

      const prefs = getNotifPrefs();
      checkPriceAlerts(price, prefs);
      checkSessionAlerts(prefs);
    } catch (err) {
      retriesRef.current += 1;
      if (retriesRef.current >= 3) {
        dispatch({ type: A.PRICE_ERROR, payload: err.message });
      }
    }
  }, [dispatch]); // dispatch is stable — no retries state, no loop multiplication

  useEffect(() => {
    fetchPrice();

    const schedule = () => {
      intervalRef.current = setTimeout(() => {
        fetchPrice();
        schedule();
      }, getPollInterval());
    };

    schedule();

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [fetchPrice]);

  return {
    price:         state.currentPrice,
    priceChange:   state.priceChange,
    session:       state.currentSession,
    priceLoading:  state.priceLoading,
    priceError:    state.priceError,
    refetch:       fetchPrice,
    priceDisplay:  state.currentPrice
      ? `$${state.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '— —',
    changeDisplay: state.priceChange
      ? `${state.priceChange >= 0 ? '+' : ''}${state.priceChange.toFixed(2)}`
      : null,
    isUp:          state.priceChange > 0,
    isDown:        state.priceChange < 0,
  };
};
